import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ExternalLink, Landmark } from "lucide-react";
import { isUsDepositAccount } from "@nh/shared";
import { Button } from "@/shared/ui/core";
import type { CheckoutResult } from "@/features/cart/cart.checkout";

export interface CheckoutSuccessProps {
  result: CheckoutResult;
}

/**
 * La confirmación del pedido — la pantalla entera, no un aviso en un
 * cajón. El folio es lo que la persona necesita guardar; lo que sigue
 * depende del método:
 *
 *   · tarjeta con link  → el botón a Stripe.
 *   · tarjeta sin llaves → se dice con todas sus letras.
 *   · transferencia / efectivo → las instrucciones de siempre.
 *
 * En los tres casos el pago se VALIDA MANUALMENTE en el panel (Anahí,
 * 26-ago) y aquí se dice, para que nadie espere un cambio automático.
 */
export function CheckoutSuccess({ result }: CheckoutSuccessProps) {
  const { t } = useTranslation();
  const { order, payment, sellerAccount } = result;
  const esVentaDeTienda = Boolean(order.storeSlug);

  return (
    <div className="mx-auto max-w-xl py-10 text-center">
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-ds-ok-bg text-ds-ok-t">
        <CheckCircle2 className="h-7 w-7" />
      </span>

      <h1 className="mt-5 text-[26px] font-semibold text-ds-ink">
        {t("¡Pedido registrado!")}
      </h1>
      <p className="mt-2 text-[15px] text-ds-ink-3">
        {t("Guarda tu folio: es como daremos seguimiento a tu pedido.")}
      </p>
      <p className="mt-4 inline-block rounded-2xl border border-ds-border bg-white px-6 py-3 font-mono text-[20px] font-semibold text-ds-ink shadow-ds-float">
        {order.folio}
      </p>

      <div className="mt-6 grid gap-3 text-left">
        {payment.status === "ready" && (
          <div className="rounded-2xl border border-ds-border bg-white p-5">
            <p className="text-[14px] font-semibold text-ds-ink">
              {t("Paga con el enlace seguro de Stripe")}
            </p>
            <Button
              as="a"
              href={payment.url}
              target="_blank"
              rel="noreferrer"
              size="lg"
              leftIcon={<ExternalLink />}
              className="mt-3"
            >
              {t("Pagar ahora")}
            </Button>
            <p className="mt-3 text-[12px] leading-relaxed text-ds-ink-3">
              {t(
                "Tu pago se validará manualmente: en cuanto lo confirmemos, tu pedido cambiará de estado.",
              )}
            </p>
          </div>
        )}

        {payment.status === "not_configured" && (
          <div className="rounded-2xl border border-ds-border bg-white p-5">
            <p className="text-[14px] font-semibold text-ds-ink">
              {t("El pago con tarjeta aún no está disponible")}
            </p>
            <p className="mt-2 text-[13px] leading-relaxed text-ds-ink-3">
              {t(
                "Tu pedido quedó registrado. Te contactaremos para completar el pago, o puedes pagar por transferencia y enviarnos tu comprobante.",
              )}
            </p>
          </div>
        )}

        {payment.status === "manual" && (
          <div className="rounded-2xl border border-ds-border bg-white p-5">
            <p className="text-[14px] font-semibold text-ds-ink">
              {order.paymentMethod === "cash"
                ? t("Pagarás en efectivo")
                : t("Pagarás por transferencia")}
            </p>
            <p className="mt-2 text-[13px] leading-relaxed text-ds-ink-3">
              {order.paymentMethod === "cash"
                ? esVentaDeTienda
                  ? t(
                      "Coordina la entrega y el pago directamente con quien te vende. Al confirmar tu pago, tu pedido cambiará de estado.",
                    )
                  : /*
                      El camino REAL del efectivo (Anahí, 29-ago): se
                      entrega a un súper socio y es su validación la que
                      aprueba la compra. Decirlo así evita la pregunta
                      que llegaba por WhatsApp —"¿y a quién le pago?"— y
                      deja claro que el pedido no avanza solo.
                    */
                    t(
                      "Entrega tu pago en efectivo a un súper socio. En cuanto valide tu pago, tu compra se aprueba y tu pedido cambia de estado.",
                    )
                : esVentaDeTienda
                  ? t(
                      "Transfiere a la cuenta de la tienda y comparte tu comprobante con quien te vende: al registrarlo, tu pedido cambiará de estado.",
                    )
                  : t(
                      "Haz tu transferencia y CONSERVA el comprobante: con él validaremos tu pago y tu pedido cambiará de estado.",
                    )}
            </p>

            {/* ── La cuenta del FRANQUICIATARIO (Anahí, 27-ago) ────────
                En pedidos de tienda con transferencia, aquí está a
                dónde va el dinero — la cuenta que la dueña registró en
                su perfil. Sin cuenta registrada, el camino es su
                WhatsApp. */}
            {order.paymentMethod === "transfer" && (
              sellerAccount ? (
                <div className="mt-4 rounded-2xl bg-ds-mist/50 p-4">
                  <p className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-ds-label text-ds-ink-3">
                    <Landmark className="h-4 w-4 text-ds-violet-pop" />
                    {t("Transfiere a esta cuenta")}
                  </p>
                  <dl className="mt-2 grid gap-1 text-[14px]">
                    <div className="flex justify-between gap-3">
                      <dt className="text-ds-ink-3">{t("Banco")}</dt>
                      <dd className="font-medium text-ds-ink">{sellerAccount.bankName}</dd>
                    </div>
                    {/* EE. UU. (pedido en USD): cuenta + routing para ACH.
                        México: la CLABE. La elige el servidor por moneda. */}
                    {isUsDepositAccount(sellerAccount) ? (
                      <>
                        <div className="flex justify-between gap-3">
                          <dt className="text-ds-ink-3">{t("Número de cuenta")}</dt>
                          <dd className="font-mono font-medium text-ds-ink">{sellerAccount.accountNumber}</dd>
                        </div>
                        <div className="flex justify-between gap-3">
                          <dt className="text-ds-ink-3">{t("Routing number (ACH)")}</dt>
                          <dd className="font-mono font-medium text-ds-ink">{sellerAccount.routingNumber}</dd>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between gap-3">
                        <dt className="text-ds-ink-3">{t("CLABE")}</dt>
                        <dd className="font-mono font-medium text-ds-ink">{sellerAccount.clabe}</dd>
                      </div>
                    )}
                    <div className="flex justify-between gap-3">
                      <dt className="text-ds-ink-3">{t("Titular")}</dt>
                      <dd className="font-medium text-ds-ink">{sellerAccount.holderName}</dd>
                    </div>
                    {isUsDepositAccount(sellerAccount) && sellerAccount.address ? (
                      <div className="flex justify-between gap-3">
                        <dt className="text-ds-ink-3">{t("Dirección del titular")}</dt>
                        <dd className="text-right font-medium text-ds-ink">{sellerAccount.address}</dd>
                      </div>
                    ) : null}
                  </dl>
                  {/* Instrucciones extra que se capturaron con la cuenta:
                      referencia, horarios, a quién avisar. */}
                  {sellerAccount.note ? (
                    <p className="mt-2 text-[12.5px] leading-relaxed text-ds-ink-3">
                      {sellerAccount.note}
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="mt-3 rounded-2xl bg-amber-500/10 px-4 py-3 text-[13px] text-amber-700">
                  {esVentaDeTienda
                    ? t(
                        "La tienda aún no registra su cuenta bancaria: pide los datos por su WhatsApp antes de transferir.",
                      )
                    : t(
                        "Todavía no hay una cuenta registrada para transferencias: escríbenos por WhatsApp y te la damos.",
                      )}
                </p>
              )
            )}
          </div>
        )}
      </div>

      <Button as={Link} to="/shop" variant="secondary" size="lg" className="mt-8">
        {t("Seguir comprando")}
      </Button>
    </div>
  );
}
